/*
================================================================================
File: src/pages/HistoryPages.js
Description: Displays history for skip traces or lead queries.
================================================================================
*/
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useAdaptiveMMKV } from "../hooks/useAdaptiveMMKV";
import moment from "moment";
import instance from "../api/axios";
import { storage } from "../App";
import PageContent from "../components/common/PageContent";
import Modal from "../components/common/Modal";

const StatCard = ({ title, value, icon, accent }) => (
  <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md border border-gray-200 dark:border-gray-700">
    <div className="flex items-center justify-between mb-3">
      <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
        {title}
      </span>
      {icon && <div className={`p-2 rounded-lg ${accent}`}>{icon}</div>}
    </div>
    <p className="text-3xl font-bold text-gray-800 dark:text-gray-100">
      {value}
    </p>
  </div>
);

const HistoryPages = ({ view, user }) => {
  const [historyData, setHistoryData] = useAdaptiveMMKV(
    "user.Feed.queryHistory",
    storage,
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [error, setError] = useState(null);
  const [selectedRows, setSelectedRows] = useState(new Set());

  const { type } = React.useMemo(() => {
    return { feedKey: "user.Feed.queryHistory", type: "queryHistory" };
  }, []);

  const pullHistoryData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await instance.get(
        `/LoadHomes.php?u=${user?.app_id}&type=${type}`,
      );
      const sortedData = [...response.data].sort((a, b) =>
        String(b.timestamp).localeCompare(String(a.timestamp)),
      );
      setHistoryData(sortedData);
      setError(null);
    } catch (e) {
      if (e.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        setError(
          "Error loading history data: " +
            (e.response?.data?.message || e.message),
        );
        console.error(
          "Error loading history data: ",
          e.response?.data?.message || e.message,
        );
      } else if (e.request) {
        // The request was made but no response was received
        setError("Error: No response received from server.");
        console.error("Error: ", e.request);
      } else {
        // Something happened in setting up the request that triggered an Error
        setError("Error: " + e.message);
        console.error("Error: ", e.message);
      }
    } finally {
      setLoading(false);
    }
  }, [user.app_id, type, setHistoryData]);

  useEffect(() => {
    pullHistoryData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const deleteSingle = async (pid) => {
    const formData = new FormData();
    formData.append("owner", user?.app_id);
    formData.append("type", type);
    formData.append("pid", pid);
    try {
      await instance.post("/deleteSingleHistory.php", formData);
      setError(null);
    } catch (e) {
      if (e.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        setError(
          "Error deleting history item: " +
            (e.response?.data?.message || e.message),
        );
        console.error(
          "Error deleting history item: ",
          e.response?.data?.message || e.message,
        );
      } else if (e.request) {
        // The request was made but no response was received
        setError("Error: No response received from server.");
        console.error("Error: ", e.request);
      } else {
        // Something happened in setting up the request that triggered an Error
        setError("Error: " + e.message);
        console.error("Error: ", e.message);
      }
    } finally {
      await pullHistoryData();
    }
  };

  const toggleRowSelection = (pid) => {
    const newSelected = new Set(selectedRows);
    if (newSelected.has(pid)) {
      newSelected.delete(pid);
    } else {
      newSelected.add(pid);
    }
    setSelectedRows(newSelected);
  };

  const toggleSelectAll = () => {
    if (selectedRows.size === currentItems.length) {
      setSelectedRows(new Set());
    } else {
      const allPids = new Set(currentItems.map((item) => item.pid));
      setSelectedRows(allPids);
    }
  };

  const deleteSelected = async () => {
    if (selectedRows.size === 0) return;
    setLoading(true);
    try {
      for (const pid of selectedRows) {
        const formData = new FormData();
        formData.append("owner", user?.app_id);
        formData.append("type", type);
        formData.append("pid", pid);
        await instance.post("/deleteSingleHistory.php", formData);
      }
      setSelectedRows(new Set());
      setError(null);
      await pullHistoryData();
    } catch (e) {
      if (e.response) {
        setError(
          "Error deleting selected items: " +
            (e.response?.data?.message || e.message),
        );
        console.error(
          "Error deleting selected items: ",
          e.response?.data?.message || e.message,
        );
      } else if (e.request) {
        setError("Error: No response received from server.");
        console.error("Error: ", e.request);
      } else {
        setError("Error: " + e.message);
        console.error("Error: ", e.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const deleteAll = async () => {
    setLoading(true);
    const formData = new FormData();
    formData.append("owner", user?.app_id);
    formData.append("type", type);
    try {
      await instance.post("/deleteHistory.php", formData);
      setError(null);
      setSelectedRows(new Set());
    } catch (e) {
      if (e.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        setError(
          "Error deleting all history: " +
            (e.response?.data?.message || e.message),
        );
        console.error(
          "Error deleting all history: ",
          e.response?.data?.message || e.message,
        );
      } else if (e.request) {
        // The request was made but no response was received
        setError("Error: No response received from server.");
        console.error("Error: ", e.request);
      } else {
        // Something happened in setting up the request that triggered an Error
        setError("Error: " + e.message);
        console.error("Error: ", e.message);
      }
    } finally {
      setLoading(false);
      await pullHistoryData();
    }
  };

  const handleRowClick = (item) => {
    setSelectedItem(item);
    setIsModalOpen(true);
  };

  // Memoize filtered history to prevent unnecessary recalculations
  const filteredHistory = useMemo(
    () =>
      historyData?.filter((item) =>
        String(item.query_address || item.skip_address)
          .toLowerCase()
          .includes(searchTerm.toLowerCase()),
      ) || [],
    [historyData, searchTerm],
  );

  // Compute history stats
  const historyStats = useMemo(() => {
    if (!historyData || historyData.length === 0) {
      return {
        total: 0,
        thisWeek: 0,
        thisMonth: 0,
        showing: filteredHistory.length,
      };
    }

    const now = moment().unix();
    const sevenDaysAgo = now - 7 * 24 * 60 * 60;
    const thirtyDaysAgo = now - 30 * 24 * 60 * 60;

    const thisWeek = historyData.filter(
      (item) => parseInt(item.timestamp) > sevenDaysAgo,
    ).length;

    const thisMonth = historyData.filter(
      (item) => parseInt(item.timestamp) > thirtyDaysAgo,
    ).length;

    return {
      total: historyData.length,
      thisWeek,
      thisMonth,
      showing: filteredHistory.length,
    };
  }, [historyData, filteredHistory]);

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredHistory.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredHistory.length / itemsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const renderPageNumbers = () => {
    const pageNumbers = [];
    const maxPageButtons = 10;
    const halfMax = Math.floor(maxPageButtons / 2);
    let startPage = Math.max(1, currentPage - halfMax);
    let endPage = Math.min(totalPages, currentPage + halfMax);

    if (currentPage <= halfMax) {
      endPage = Math.min(totalPages, maxPageButtons);
    }
    if (currentPage + halfMax > totalPages) {
      startPage = Math.max(1, totalPages - maxPageButtons + 1);
    }

    if (startPage > 1) {
      pageNumbers.push(
        <span key="ellipsis-start" className="px-2">
          ...
        </span>,
      );
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(
        <button
          key={i}
          onClick={() => paginate(i)}
          className={`
            w-8 h-8 rounded-full text-sm font-medium mx-1 transition-colors duration-200
            ${
              currentPage === i
                ? "bg-blue-600 text-white shadow-md"
                : "bg-gray-200 text-gray-700 hover:bg-gray-300"
            }
          `}
        >
          {i}
        </button>,
      );
    }

    if (endPage < totalPages) {
      pageNumbers.push(
        <span key="ellipsis-end" className="px-2">
          ...
        </span>,
      );
    }

    return pageNumbers;
  };

  return (
    <PageContent title="History" subtitle={"AI Query History"} error={error}>
      <div className="p-6 space-y-6">
        {/* Stat Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <StatCard
            title="Total Records"
            value={historyStats.total}
            icon={
              <svg
                className="w-6 h-6 text-blue-600 dark:text-blue-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V7M16 3h5v5M2 3h5v5"
                />
              </svg>
            }
            accent="bg-blue-100 dark:bg-blue-900/30"
          />
          <StatCard
            title="This Week"
            value={historyStats.thisWeek}
            icon={
              <svg
                className="w-6 h-6 text-green-600 dark:text-green-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
            }
            accent="bg-green-100 dark:bg-green-900/30"
          />
          <StatCard
            title="This Month"
            value={historyStats.thisMonth}
            icon={
              <svg
                className="w-6 h-6 text-orange-600 dark:text-orange-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
            }
            accent="bg-orange-100 dark:bg-orange-900/30"
          />
          <StatCard
            title="Showing"
            value={historyStats.showing}
            icon={
              <svg
                className="w-6 h-6 text-indigo-600 dark:text-indigo-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M3 4h18M3 8h18M3 12h18M3 16h18"
                />
              </svg>
            }
            accent="bg-indigo-100 dark:bg-indigo-900/30"
          />
        </div>

        {/* Table Card */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700">
          <div className="p-4 flex flex-col sm:flex-row justify-between items-center gap-4 bg-gray-50 dark:bg-gray-700 rounded-t-lg border-b border-gray-200 dark:border-gray-600">
            <div className="relative flex-1">
              <svg
                className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
                className="block w-full pl-10 p-2 border border-gray-300 rounded-md text-sm bg-white dark:bg-gray-600 dark:text-gray-100 dark:border-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
                placeholder="Search History by Address..."
              />
            </div>
            <div className="flex gap-2">
              {selectedRows.size > 0 && (
                <>
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300 py-2">
                    {selectedRows.size} selected
                  </span>
                  <button
                    onClick={deleteSelected}
                    disabled={loading}
                    className="px-4 py-2 text-sm font-medium rounded-lg text-white bg-red-600 hover:bg-red-700 dark:bg-red-600 dark:hover:bg-red-700 disabled:opacity-50"
                  >
                    {loading ? "Deleting..." : "Delete Selected"}
                  </button>
                </>
              )}
              {selectedRows.size === 0 && (
                <button
                  onClick={deleteAll}
                  disabled={loading}
                  className="px-4 py-2 text-sm font-medium rounded-lg text-white bg-red-600 hover:bg-red-700 dark:bg-red-600 dark:hover:bg-red-700"
                >
                  {loading ? "Deleting..." : "Delete All"}
                </button>
              )}
            </div>
          </div>
          <div className="overflow-x-visible">
            <table className="w-full text-sm text-left ">
              <thead className="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-300 border-b border-gray-200 dark:border-gray-600">
                <tr>
                  <th className="px-4 py-3 w-12">
                    <input
                      type="checkbox"
                      checked={
                        selectedRows.size === currentItems.length &&
                        currentItems.length > 0
                      }
                      onChange={toggleSelectAll}
                      className="cursor-pointer"
                    />
                  </th>
                  <th className="px-6 py-3">Name</th>
                  <th className="px-6 py-3">Address</th>
                  <th className="px-6 py-3">Recorded</th>
                  <th className="px-6 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentItems.length > 0 ? (
                  currentItems.map((item) => (
                    <tr
                      key={item.pid}
                      className="bg-white border-b border-gray-200 hover:bg-gray-50 dark:bg-gray-800 dark:border-gray-700 dark:hover:bg-gray-700 dark:text-gray-200"
                    >
                      <td className="px-4 py-4">
                        <input
                          type="checkbox"
                          checked={selectedRows.has(item.pid)}
                          onChange={() => toggleRowSelection(item.pid)}
                          className="cursor-pointer"
                        />
                      </td>
                      <td
                        className="px-6 py-4 font-medium cursor-pointer"
                        onClick={() => handleRowClick(item)}
                      >
                        {item.skip_name || item.query_type}
                      </td>
                      <td
                        className="px-6 py-4 cursor-pointer"
                        onClick={() => handleRowClick(item)}
                      >
                        {item.query_address || item.skip_address}
                      </td>
                      <td
                        className="px-6 py-4 cursor-pointer"
                        onClick={() => handleRowClick(item)}
                      >
                        {moment.unix(item.timestamp).format("LLL")}
                      </td>
                      <td className="px-6 py-4">
                        <button
                          onClick={() => deleteSingle(item.pid)}
                          className="font-medium text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan="5"
                      className="px-6 py-4 text-center text-gray-500 dark:text-gray-400"
                    >
                      No history found for this category.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {filteredHistory.length > itemsPerPage && (
            <div className="p-4 flex items-center justify-between border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700 rounded-b-lg">
              <button
                onClick={() => paginate(currentPage - 1)}
                disabled={currentPage === 1}
                className="px-4 py-2 text-sm font-medium rounded-lg text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-600 hover:bg-gray-300 dark:hover:bg-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <div className="flex-grow flex justify-center">
                {renderPageNumbers()}
              </div>
              <button
                onClick={() => paginate(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="px-4 py-2 text-sm font-medium rounded-lg text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-600 hover:bg-gray-300 dark:hover:bg-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          )}
        </div>
      </div>
      <Modal
        show={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title="History Item Details"
        icon={
          <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H7a1 1 0 01-1-1v-6z"
              clipRule="evenodd"
            />
          </svg>
        }
        iconColor="blue"
      >
        {selectedItem && (
          <textarea
            readOnly
            className="w-full h-96 p-2 border border-gray-300 dark:border-gray-600 rounded-md bg-gray-50 dark:bg-gray-800 dark:text-gray-200"
            defaultValue={JSON.stringify(
              JSON.parse(selectedItem.query_data),
              null,
              2,
            )}
          />
        )}
      </Modal>
    </PageContent>
  );
};

export default HistoryPages;
